import { exceptions } from '../../utils/exceptions'
import { JSONAPIErrors } from '../../api/types'

export async function uploadAttachments(
  files: File[],
  stage?: string
): Promise<string[]> {
  const uploadPromises = files.map(async (file) => {
    try {
      const base64String = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const result = reader.result?.toString()
          if (!result) {
            reject(new Error('Failed to read file'))
            return
          }

          const base64 = result.replace('data:', '').replace(/^.+,/, '')
          resolve(base64)
        }
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsDataURL(file)
      })

      const response = await fetch('/api/v1/blobs/create_attachment', {
        method: 'POST',
        body: JSON.stringify({
          data: {
            type: 'blobs',
            attributes: {
              data: base64String,
              filename: file.name,
              content_type: file.type,
              stage: stage,
            },
          },
        }),
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const json = await response.json()
      if (json.errors && json.errors.length > 0) {
        const errorMessage = json.errors[0]?.detail || 'Upload failed'
        throw new Error(errorMessage)
      }

      const signedId = json.data?.attributes?.signed_id || json.data?.id
      if (!signedId) {
        throw new Error('No signed ID returned from server')
      }

      return signedId
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      throw new Error(errorMessage)
    }
  })

  return await Promise.all(uploadPromises)
}

export class AttachmentUploadVm {
  constructor(
    private afterUpload: (blobId: string) => void,
    private onUpload?: () => void,
    private onUploadError?: (errors: JSONAPIErrors['errors']) => void
  ) {}

  async onFileSelected(
    base64String: string,
    filename: string,
    contentType: string
  ) {
    this.onUpload?.()

    const body = {
      data: {
        type: 'blobs',
        attributes: {
          data: base64String,
          filename: filename,
          content_type: contentType,
        },
      },
    }

    try {
      const response = await fetch('/api/v1/blobs/create_attachment', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
      })
      const json = await response.json()
      if (json.errors) {
        this.onUploadError?.(json.errors)
      } else {
        const signedId = json.data?.attributes?.signed_id || json.data?.id
        if (signedId) this.afterUpload(signedId)
      }
    } catch (e) {
      const error = e as Error
      exceptions.handle(error)
    }
  }
}
